import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { dataCacheService } from '../services/DataCacheService';
import './AdminProfileView.css';

function AdminProfileView({ adminData }) {
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadProfile = useCallback(async () => {
    if (!adminData?.uid) return;

    console.log('🔥 AdminProfile: Loading profile');
    try {
      // Load from users collection first
      const userData = (await dataCacheService.getDoc(`users/${adminData.uid}`, doc(db, 'users', adminData.uid))) || {};

      // Load from admins/superAdmins collection for additional fields
      const role = userData.role || adminData.role;
      const collectionName = role === 'Super Admin' ? 'superAdmins' : 'admins';
      const roleData = (await dataCacheService.getDoc(`${collectionName}/${adminData.uid}`, doc(db, collectionName, adminData.uid))) || {};

      // Split fullName into firstName and lastName
      const fullName = roleData.fullName || userData.username || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setProfile({
        username: userData.username || fullName,
        email: userData.email || roleData.email || adminData.email || '',
        phone: roleData.phoneNumber || '',
        firstName,
        lastName
      });
    } catch (error) {
      console.error('🔥 AdminProfile: Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const role = adminData.role || '';
      const collectionName = role.toLowerCase() === 'super admin' ? 'superAdmins' : 'admins';
      const fullName = `${profile.firstName} ${profile.lastName}`.trim();

      // Update users collection (username + phone so all platforms see it)
      await updateDoc(doc(db, 'users', adminData.uid), {
        username: fullName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phone
      });

      // Update admins/superAdmins collection — setDoc+merge so it works even
      // when the role doc doesn't exist yet (updateDoc would throw).
      await setDoc(doc(db, collectionName, adminData.uid), {
        fullName,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phone,
        email: profile.email,
        role: role || 'admin',
        schoolId: adminData.schoolId || null
      }, { merge: true });
      dataCacheService.invalidatePath(`users/${adminData.uid}`);
      dataCacheService.invalidatePath(`${collectionName}/${adminData.uid}`);

      setMessage('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-profile-view">
      <div className="view-header">
        <h2>Admin Profile</h2>
      </div>

      <div className="profile-card">
        <div className="profile-avatar">
          <span>👑</span>
        </div>
        <div className="profile-info">
          <h3>{profile.firstName} {profile.lastName}</h3>
          <p>{profile.email}</p>
          <span className="role-badge">Admin</span>
        </div>
      </div>

      <form onSubmit={handleSave} className="profile-form">
        <h3>Personal Information</h3>
        
        <div className="form-row">
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={profile.firstName}
              onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={profile.lastName}
              onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={profile.username}
            onChange={(e) => setProfile({ ...profile, username: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={profile.email}
            disabled
          />
          <small>Email cannot be changed</small>
        </div>

        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={profile.phone}
            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
          />
        </div>

        {message && (
          <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminProfileView;
